import { useEffect, useMemo, useRef, useState } from 'react'

export type LightboxPhoto = {
  id: string
  image_url: string
  caption?: string
  is_featured?: boolean
}

export default function Lightbox({
  isOpen,
  photos,
  startIndex,
  onClose,
}: {
  isOpen: boolean
  photos: LightboxPhoto[]
  startIndex: number
  onClose: () => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)
  const clampIndex = (i: number, len: number) => {
    const n = Math.max(0, len - 1)
    const s = Number.isFinite(i) ? i : 0
    return Math.max(0, Math.min(n, s))
  }
  const [index, setIndex] = useState(() => clampIndex(startIndex, photos.length))

  const clampedStart = useMemo(() => {
    return clampIndex(startIndex, photos.length)
  }, [photos.length, startIndex])

  useEffect(() => {
    if (!isOpen) return
    setIndex(clampedStart)
  }, [isOpen, clampedStart])

  useEffect(() => {
    if (!isOpen) return
    lastFocusRef.current = (document.activeElement as any) || null
    // Focus the dialog container on open for keyboard navigation.
    window.setTimeout(() => containerRef.current?.focus(), 0)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) return
    // Restore focus after close (best effort).
    window.setTimeout(() => lastFocusRef.current?.focus?.(), 0)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setIndex((i) => Math.max(0, i - 1))
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setIndex((i) => Math.min(photos.length - 1, i + 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose, photos.length])

  const current = photos[index] || null
  if (!isOpen || !current) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="relative w-full max-w-4xl rounded-2xl border border-white/10 bg-neutral-950 p-4 text-white shadow-xl outline-none"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-white/60">
            {index + 1} / {photos.length}
            {current.is_featured ? <span className="ml-2 text-amber-200">Featured</span> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs"
          >
            Close
          </button>
        </div>

        <div className="mt-3">
          <img
            src={current.image_url}
            alt={current.caption || 'Photo'}
            className="w-full max-h-[70vh] object-contain rounded-xl bg-black/40"
          />
        </div>

        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="text-sm text-white/85 whitespace-pre-wrap">
            {current.caption ? current.caption : <span className="text-white/55">No caption</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index <= 0}
              className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-semibold disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(photos.length - 1, i + 1))}
              disabled={index >= photos.length - 1}
              className="px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
